// ---------------------------------------------------------------------------
// Responsive utilities — breakpoint widths and responsive override resolution.
// ---------------------------------------------------------------------------

import type { AppTarget, Breakpoint } from '@/features/page-editor/store/types'

/** Canonical pixel widths for each breakpoint. */
export const BREAKPOINTS: Record<Breakpoint, number> = {
  mobile: 375,
  tablet: 768,
  desktop: 1280,
}

/** Canonical viewport sizes for framed Preview/Page rendering. */
export const BREAKPOINT_VIEWPORTS: Record<Breakpoint, { width: number; height: number }> = {
  mobile: { width: 375, height: 812 },
  tablet: { width: 768, height: 1024 },
  desktop: { width: 1280, height: 800 },
}

/** Human-readable labels for each breakpoint. */
export const BREAKPOINT_LABELS: Record<Breakpoint, string> = {
  mobile: 'Mobile',
  tablet: 'Tablet',
  desktop: 'Desktop',
}

/** Default settings per app target. */
export const APP_TARGET_DEFAULTS: Record<
  AppTarget,
  {
    defaultBreakpoint: Breakpoint
  }
> = {
  mobile: {
    defaultBreakpoint: 'mobile',
  },
  web: {
    defaultBreakpoint: 'desktop',
  },
  desktop: {
    defaultBreakpoint: 'desktop',
  },
}

/**
 * The frame an imported application OPENS in, keyed by the converter's own
 * `kind`.
 *
 * `kind` is not a Studio invention: `cogo-convert` writes it into the Cogo
 * application manifest (`packages/cogo-convert/src/source/app-package.ts`,
 * `kind: result.manifest.kind`) from its `SourceAppKind` union
 * (`react-web | expo-router | react-admin`), and both live appliance
 * projects carry it — `SIPASS.app` reads back `expo-router`, `SIPASS.co`
 * reads back `react-web`. Only the expo lane is a phone application; the two
 * react lanes are browser applications and keep the desktop frame.
 *
 * Unknown or absent kinds deliberately return `null` so the caller falls
 * through to its own default rather than guessing.
 */
export const SOURCE_APP_KIND_BREAKPOINTS: Record<string, Breakpoint> = {
  'expo-router': 'mobile',
  'react-web': 'desktop',
  'react-admin': 'desktop',
}

/** Breakpoint an imported app's converter `kind` implies, or null if it says nothing. */
export function breakpointForSourceAppKind(kind: unknown): Breakpoint | null {
  if (typeof kind !== 'string') return null
  return SOURCE_APP_KIND_BREAKPOINTS[kind] ?? null
}

/**
 * Resolve a value with responsive overrides using a cascading merge.
 *
 * Merge order (largest first, most-specific last wins):
 *   mobile:  base <- desktop <- tablet <- mobile
 *   tablet:  base <- desktop <- tablet
 *   desktop: base <- desktop
 */
export function resolveResponsive<T>(
  base: T,
  responsive: Partial<Record<Breakpoint, Partial<T>>> | undefined,
  breakpoint: Breakpoint,
): T {
  if (!responsive) return base

  // Chain from largest (least-specific) to smallest (most-specific)
  const chain: Breakpoint[] =
    breakpoint === 'mobile'
      ? ['desktop', 'tablet', 'mobile']
      : breakpoint === 'tablet'
        ? ['desktop', 'tablet']
        : ['desktop']

  let result = { ...base }
  for (const bp of chain) {
    if (responsive[bp]) {
      result = { ...result, ...responsive[bp] }
    }
  }
  return result
}
